import { ChevronLeft, ChevronRight, Plus } from 'lucide-react';

const BRAND = '#8B80F9';

export type MonthCalendarEvent = {
  id: string;
  scheduled_at: string;
  label: string;
};

function sameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;

function buildMonthGrid(year: number, monthIndex: number): (Date | null)[] {
  const first = new Date(year, monthIndex, 1);
  const startPad = first.getDay();
  const daysInMonth = new Date(year, monthIndex + 1, 0).getDate();
  const cells: (Date | null)[] = [];
  for (let i = 0; i < startPad; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, monthIndex, d));
  // Always render exactly 6 rows (42 cells) so the grid height is stable
  while (cells.length < 42) cells.push(null);
  return cells;
}

export function InterviewMonthCalendar(props: {
  monthAnchor: Date;
  onMonthAnchorChange: (next: Date) => void;
  events: MonthCalendarEvent[];
  selectedId: string | null;
  onSelectEvent: (id: string) => void;
  onDayClick: (day: Date) => void;
  /** When true, past dates still fire onDayClick (e.g. AI interview calendar). Default false — past days disabled. */
  allowPastDayClick?: boolean;
}) {
  const { monthAnchor, onMonthAnchorChange, events, selectedId, onSelectEvent, onDayClick, allowPastDayClick = false } = props;

  const y = monthAnchor.getFullYear();
  const m = monthAnchor.getMonth();
  const cells = buildMonthGrid(y, m);

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const title    = monthAnchor.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
  const prevMonth = () => onMonthAnchorChange(new Date(y, m - 1, 1));
  const nextMonth = () => onMonthAnchorChange(new Date(y, m + 1, 1));
  const goToday   = () => onMonthAnchorChange(new Date(today.getFullYear(), today.getMonth(), 1));
  const isPast    = (d: Date) => d < today;

  return (
    <div className="rounded-xl border vscode-border overflow-hidden w-full" style={{ background: 'var(--vscode-bg-primary)' }}>

      {/* ── Header ── */}
      <div className="flex items-center justify-between gap-2 px-4 py-3 border-b vscode-border">
        <button type="button" onClick={prevMonth}
          className="p-1.5 rounded-lg hover:vscode-bg-hover vscode-text-secondary transition-colors"
          aria-label="Previous month">
          <ChevronLeft className="w-4 h-4" />
        </button>

        <div className="flex items-center gap-3">
          <p className="text-sm font-semibold vscode-text-primary">{title}</p>
          <button type="button" onClick={goToday}
            className="text-[10px] px-2.5 py-0.5 rounded-full border vscode-border vscode-text-tertiary hover:vscode-text-primary transition-colors">
            Today
          </button>
        </div>

        <button type="button" onClick={nextMonth}
          className="p-1.5 rounded-lg hover:vscode-bg-hover vscode-text-secondary transition-colors"
          aria-label="Next month">
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>

      {/* ── Weekday labels ── */}
      <div className="grid grid-cols-7 border-b vscode-border">
        {WEEKDAYS.map((wd) => (
          <div key={wd} className="text-center text-[10px] font-semibold uppercase tracking-wide vscode-text-tertiary py-2">
            {wd}
          </div>
        ))}
      </div>

      {/* ── Day cells — always 6 full rows ── */}
      <div className="grid grid-cols-7" style={{ gridAutoRows: '1fr' }}>
        {cells.map((cell, idx) => {
          const isLastCol = idx % 7 === 6;
          const isLastRow = idx >= 35; // last 7 cells

          if (!cell) {
            return (
              <div key={`pad-${idx}`}
                className={`border-r border-b vscode-border ${isLastCol ? 'border-r-0' : ''} ${isLastRow ? 'border-b-0' : ''}`}
                style={{ minHeight: 90, opacity: 0.25 }}
              />
            );
          }

          const dayEvents = events.filter((ev) => sameDay(new Date(ev.scheduled_at), cell));
          const isToday   = sameDay(cell, today);
          const past      = isPast(cell);
          const dayClickDisabled = past && !allowPastDayClick;

          return (
            <div key={cell.getTime()}
              className={[
                'flex flex-col border-r border-b vscode-border transition-colors',
                isLastCol  ? 'border-r-0'  : '',
                isLastRow  ? 'border-b-0'  : '',
                isToday    ? 'bg-brand-500/5' : '',
                past && !allowPastDayClick ? 'opacity-50' : '',
              ].join(' ')}
              style={{ minHeight: 90 }}
            >
              {/* Day number */}
              <button type="button"
                className="text-left w-full px-2 pt-1.5 pb-0.5 group flex items-center gap-1"
                onClick={() => {
                  if (!dayClickDisabled) onDayClick(cell);
                }}
                disabled={dayClickDisabled}
                title={
                  dayClickDisabled
                    ? 'Cannot schedule in the past'
                    : 'Open Schedule Interview (AI)'
                }>
                <span
                  className="inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-semibold shrink-0"
                  style={isToday
                    ? { background: BRAND, color: '#fff' }
                    : { color: 'var(--vscode-text-primary)' }}>
                  {cell.getDate()}
                </span>
                {!dayClickDisabled && (
                  <Plus className="w-3 h-3 opacity-0 group-hover:opacity-60 transition-opacity shrink-0"
                    style={{ color: BRAND }} />
                )}
              </button>

              {/* Event chips */}
              <div className="flex flex-col gap-0.5 px-1 pb-1.5 overflow-hidden">
                {dayEvents.slice(0, 3).map((ev) => (
                  <button key={ev.id} type="button"
                    title={ev.label}
                    onClick={(e) => { e.stopPropagation(); onSelectEvent(ev.id); }}
                    className={`text-left truncate rounded px-1.5 py-0.5 text-[10px] leading-tight font-medium transition-colors
                      ${selectedId === ev.id ? 'text-white' : 'vscode-text-secondary hover:vscode-text-primary'}`}
                    style={selectedId === ev.id
                      ? { background: BRAND }
                      : { background: 'var(--vscode-bg-tertiary)' }}>
                    {new Date(ev.scheduled_at).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}
                    {' '}{ev.label}
                  </button>
                ))}
                {dayEvents.length > 3 && (
                  <span className="text-[9px] vscode-text-tertiary pl-1.5">+{dayEvents.length - 3} more</span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
